import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Keep in sync with backend ALLOWED_EMOJIS in routers/reactions.py
const REACTIONS = [
  { key: 'like',  char: '👍', label: 'Sviđa mi se' },
  { key: 'love',  char: '❤️', label: 'Srce' },
  { key: 'haha',  char: '😂', label: 'Smijeh' },
  { key: 'wow',   char: '😮', label: 'Oduševljenje' },
  { key: 'sad',   char: '😢', label: 'Tuga' },
  { key: 'angry', char: '😡', label: 'Ljutnja' },
];

export default function ArticleReactions({ slug }) {
  const [counts, setCounts] = useState({});
  const [mine, setMine] = useState(null);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/public/articles/${slug}/reactions`);
      if (!r.ok) return;
      const d = await r.json();
      setCounts(d.counts || {});
      setMine(d.mine || null);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  async function react(emojiKey) {
    if (busy) return;
    setBusy(emojiKey);
    // Optimistic update
    const prev = { counts: { ...counts }, mine };
    const next = { ...counts };
    if (mine && mine !== emojiKey) next[mine] = Math.max(0, (next[mine] || 0) - 1);
    if (mine === emojiKey) {
      next[emojiKey] = Math.max(0, (next[emojiKey] || 0) - 1);
      setMine(null);
    } else {
      next[emojiKey] = (next[emojiKey] || 0) + 1;
      setMine(emojiKey);
    }
    setCounts(next);

    try {
      const r = await fetch(`${API}/public/articles/${slug}/reactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji: emojiKey }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Greška');
      setCounts(d.counts || {});
      setMine(d.mine || null);
    } catch (e) {
      // revert
      setCounts(prev.counts);
      setMine(prev.mine);
      toast.error(e.message || 'Reakcija nije zapisana.');
    } finally {
      setBusy(null);
    }
  }

  if (loading) return null;
  const total = Object.values(counts).reduce((a, b) => a + (b || 0), 0);

  return (
    <section
      className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-800"
      data-testid="article-reactions"
    >
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 dark:text-gray-100 border-l-4 border-red-600 pl-3">
          Kako vam se svidio članak?
        </h3>
        <span className="text-xs text-gray-500" data-testid="reactions-total">
          {total} {total === 1 ? 'reakcija' : (total % 10 >= 2 && total % 10 <= 4 && (total % 100 < 10 || total % 100 >= 20)) ? 'reakcije' : 'reakcija'}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {REACTIONS.map(r => {
          const n = counts[r.key] || 0;
          const active = mine === r.key;
          return (
            <button
              key={r.key}
              type="button"
              onClick={() => react(r.key)}
              disabled={!!busy}
              aria-label={r.label}
              title={r.label}
              data-testid={`react-${r.key}`}
              className={`group flex items-center gap-2 px-3.5 py-2 rounded-full border transition-all duration-150
                ${active
                  ? 'bg-red-50 dark:bg-red-600/15 border-red-500 shadow-sm scale-105'
                  : 'bg-gray-50 dark:bg-[#111827] border-gray-200 dark:border-gray-800 hover:border-red-400 hover:-translate-y-0.5'}
                disabled:opacity-60`}
            >
              <span className="text-lg leading-none" aria-hidden="true">{r.char}</span>
              <span className={`text-xs font-semibold tabular-nums ${active ? 'text-red-600 dark:text-red-300' : 'text-gray-600 dark:text-gray-300'}`}>
                {n}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
